import { claimPendingRecipe } from "authentication/utils";
import { supabase } from "lib/supabaseClient";
import { useAuthentication } from "authentication/AuthenticationContext";
import { v4 as uuidv4 } from "uuid";
import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

export const RecipeContext = createContext();

const getAuthHeaders = async (extra = {}) => {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const headers = { ...extra };
  if (session) {
    headers.Authorization = `Bearer ${session.access_token}`;
  }
  return headers;
};

export const RecipeProvider = (props) => {
  const { user } = useAuthentication();
  const [badges, setBadges] = useState([]);
  const [featureToggles, setFeatureToggles] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [justAddedBadge, setJustAddedBadge] = useState({});
  const [kitchenTools, setKitchenTools] = useState([]);
  const [macros, setMacros] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [recipes, setRecipes] = useState([]);

  const API_URL = "https://blogfreerecipe-api.onrender.com";
  //const API_URL = process.env.REACT_APP_API_URL || "https://blogfreerecipe-api.onrender.com";

  const refreshRecipes = useCallback(async () => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/recipes`, { headers });
    if (!response.ok) {
      throw new Error(`Failed to load recipes (${response.status})`);
    }
    setRecipes(await response.json());
  }, []);

  const recipesWithJustAdded = useMemo(() => {
    if (!justAddedBadge?.id) return recipes;

    const ninetyDays = 90 * 24 * 60 * 60 * 1000;
    return recipes.map((r) =>
      Date.now() - new Date(r.created_at) < ninetyDays
        ? { ...r, badges: [justAddedBadge, ...(r.badges || [])] }
        : r,
    );
  }, [recipes, justAddedBadge]);

  //for when a user signs in/ signs out, make sure the recipe lists adds/takes away any pending or private recipes that they should only see when signed in
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session) {
        setTimeout(async () => {
          await claimPendingRecipe(session.access_token);
          refreshRecipes().catch(console.error);
        }, 0);
      } else if (event === "SIGNED_OUT") {
        setTimeout(() => refreshRecipes().catch(console.error), 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [refreshRecipes]);

  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch Badges
        const badgesResponses = await fetch(`${API_URL}/api/badges`);
        const allBadges = await badgesResponses.json();
        setBadges(allBadges);
        const justAdded = allBadges.find((b) => b.name === "Just Added");
        setJustAddedBadge(justAdded);

        await refreshRecipes();

        // Fetch KitchenTools
        const kitchenToolsResponse = await fetch(
          `${API_URL}/api/kitchen-tools`,
        );
        const allKitchenTools = await kitchenToolsResponse.json();
        setKitchenTools(allKitchenTools);

        // Fetch Ingredients
        const ingredientsResponse = await fetch(`${API_URL}/api/ingredients`);
        const allIngredients = await ingredientsResponse.json();
        setIngredients(allIngredients);

        // Fetch Measurement options
        const measurementsResponse = await fetch(`${API_URL}/api/measurements`);
        const allMeasurements = await measurementsResponse.json();
        setMeasurements(allMeasurements);

        // Fetch Macros options
        const macrosResponse = await fetch(`${API_URL}/api/macros`);
        const allMacros = await macrosResponse.json();
        setMacros(allMacros);
      } catch (error) {
        console.log("This went wrong: ", error);
      }
    }
    fetchData();
  }, []);

  const saveRecipe = async (recipeData) => {
    const newId = uuidv4();
    //don't save Just Added Badge, if user selected it
    const filteredBadges = recipeData.badge_ids?.filter(
      (b) => b !== justAddedBadge.id,
    );

    // Send the user's token if they're signed in, so the server can set created_by
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(`${API_URL}/api/recipes`, {
      method: "POST",
      headers: headers,
      body: JSON.stringify({
        id: newId,
        additional_pictures: recipeData.additional_pictures,
        badge_ids: filteredBadges,
        calories: recipeData.calories || 0,
        cook_time: recipeData.cook_time || 0,
        ingredients: recipeData.ingredients,
        is_private: recipeData.is_private ?? false,
        kitchen_tools: recipeData.kitchen_tools,
        macros: recipeData.macros,
        main_picture: recipeData.main_picture,
        num_of_servings: recipeData.num_of_servings,
        preheat_temp: recipeData.preheat_temp || 0,
        prep_time: recipeData.prep_time || 0,
        recipe_name: recipeData.recipe_name,
        serving_size: recipeData.serving_size,
        steps: recipeData.instructions,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Failed to add recipe");
    }

    const data = await response.json();

    // Guest submission: keep the claim token so the recipe can be attached after sign-in
    if (data.claimToken) {
      try {
        localStorage.setItem(
          "pendingRecipeClaim",
          JSON.stringify({
            recipeId: data.recipeId,
            claimToken: data.claimToken,
          }),
        );
      } catch {
        // storage unavailable (e.g. some private browsing modes): claim just won't happen
      }
    }

    //if already signed in, then refresh the list to see recipe just added
    //(if the usesr was not signed in, and chose to, it would refresh as part of the sign in process)
    if (user) {
      await refreshRecipes();
    }

    return data;
  };

  const fetchRecipeById = useCallback(async (recipeId) => {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_URL}/api/recipes/${recipeId}`, {
      headers,
    });

    if (!response.ok) {
      throw new Error(`Unable to load recipe. Status: ${response.status}`);
    }

    return response.json();
  }, []);

  return (
    <RecipeContext.Provider
      value={{
        badges,
        featureToggles,
        fetchRecipeById,
        ingredients,
        kitchenTools,
        macros,
        measurements,
        recipes: recipesWithJustAdded,
        saveRecipe,
      }}
    >
      {props.children}
    </RecipeContext.Provider>
  );
};
